import { useEffect } from "react";

import { useSettings } from "../store/useSettings";

/**
 * Переключатель режима СИМУЛЯЦИИ и его баннер.
 *
 * # Почему один тумблер на всю полосу, а не своя форма
 *
 * Потому что проба обязана уходить тем же путём, что настоящий ордер: те же
 * режимы цены, те же пресеты суммы, те же клавиши и тот же клик по лесенке.
 * Отдельная форма измеряла бы вход, которого оператор в бою не делает, — то
 * есть отвечала бы не на тот вопрос.
 *
 * # Чем закрыт риск перепутать
 *
 * Тем, что включённый режим виден ПОСТОЯННО и в трёх местах сразу: полоса
 * баннера над страницей, перекрашенные кнопки отправки и заголовок вкладки.
 * Приём и цвет взяты у аварийного стопа сознательно — оператор уже знает, что
 * означает цветная полоса поперёк экрана, и переучиваться не придётся.
 *
 * Цвет при этом ДРУГОЙ (янтарь против красного): красный в терминале означает
 * «требуется разбирательство», а симуляция ничего не требует. Приравняв их, мы
 * приучили бы не читать красное — и настоящий отказ прошёл бы мимо глаз.
 */
export function SimSwitch({ disabled, reason }: { disabled?: boolean; reason?: string }) {
  const on = useSettings((s) => s.settings.paperMode);
  const setOn = useSettings((s) => s.setPaperMode);

  const title = disabled
    ? reason || "пробные ордера выключены на трейдере (PAPER_ENABLED)"
    : on
      ? "выключить симуляцию: кнопки и клавиши снова тратят настоящие деньги"
      : "включить симуляцию: кнопки и клавиши ставят пробные ордера, деньги не тратятся";

  return (
    <button
      type="button"
      onClick={() => setOn(!on)}
      disabled={disabled}
      className="flex shrink-0 items-center gap-1.5 rounded border px-2 py-1 text-xs font-medium disabled:opacity-40"
      style={{
        borderColor: on ? "var(--color-warn, #fbbf24)" : "var(--color-border)",
        color: on ? "var(--color-warn, #fbbf24)" : "var(--color-muted)",
      }}
      title={title}
    >
      <span aria-hidden>{on ? "◉" : "○"}</span>
      Симуляция
    </button>
  );
}

/**
 * Полоса поверх страницы, пока режим включён.
 *
 * Живёт в макете приложения, а не на странице рынка: режим глобальный, а
 * оператор уходит со страницы рынка на сделки и контрагентов и возвращается.
 * Полоса, привязанная к одной странице, пропала бы ровно тогда, когда о режиме
 * успевают забыть.
 *
 * Заголовок вкладки метится по той же причине и тем же приёмом, что у стопа:
 * терминал держат в фоне, и вкладку узнают по строке.
 */
export function SimBanner() {
  const on = useSettings((s) => s.settings.paperMode);

  useEffect(() => {
    if (!on) return;
    // Заголовок НЕ трогается, когда режим выключен: им же управляет баннер
    // аварийного стопа, и два владельца одной строки затирали бы друг друга.
    // Здесь только взведённое состояние, и оно возвращает строку при выходе.
    const before = document.title;
    document.title = `◉ СИМУЛЯЦИЯ — ${before}`;
    return () => {
      document.title = before;
    };
  }, [on]);

  if (!on) return null;

  return (
    <div className="border-b border-amber-400/60 bg-amber-400/10 px-6 py-2 text-center text-sm font-medium text-amber-300">
      СИМУЛЯЦИЯ — кнопки и клавиши ставят ПРОБНЫЕ ордера, настоящие деньги не тратятся
    </div>
  );
}
